import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  MapPin,
  Save,
  Check,
  Loader2,
  ChevronDown,
  AlertCircle,
  X,
  ArrowLeft,
  Shield,
  Users,
} from "lucide-react";
import { regionOptions, locationsByRegion } from "../../../utils/data";
import Navbar from "../../../components/Navbar";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import BranchPreview from "../../../components/Branches/BranchPreview";
import { usePermissionCheck } from "../../../hooks/permissionHooks/usePermissionCheck";
import { PERMISSIONS } from "../../../config/permissions";
import branchService from "../../../requests/branches/BranchService";

const OptionalLabel = () => (
  <span className="ml-1 text-xs text-gray-400">(optional)</span>
);

const ValidationError = ({ message }) => (
  <motion.p
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    className="flex gap-1 items-center mt-1 text-sm text-red-500"
  >
    <AlertCircle className="w-4 h-4" />
    {message}
  </motion.p>
);

const EmptyState = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex flex-col justify-center items-center p-8 text-center"
  >
    <div className="flex justify-center items-center mb-6 w-24 h-24 bg-gradient-to-r from-[#FF6F6E]/20 to-[#FFB5B4]/20 rounded-full">
      <Building2 className="w-12 h-12 text-[#FF6F6E]" />
    </div>
    <h3 className="mb-2 text-xl font-semibold text-gray-900">
      No Branches Added Yet
    </h3>
    <p className="mb-6 max-w-md text-center text-gray-500">
      Start by adding your first branch location. You can add multiple branches
      before creating your vendor profile.
    </p>
  </motion.div>
);

const BranchAdd = () => {
  const navigate = useNavigate();

  // Permission checks
  const { canPerform, isAuthenticated, isMainVendor } = usePermissionCheck();

  // Check if user can create branches
  const canCreateBranches =
    canPerform(
      PERMISSIONS.MODULES.BRANCH_MANAGEMENT,
      PERMISSIONS.ACTIONS.CREATE_BRANCH
    ) || isMainVendor;

  const [branches, setBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState({
    id: null,
    name: "",
    region: "",
    location: "",
    customLocation: "",
    district: "",
    suburb: "",
    landmark: "",
    gpsAddress: "",
    googleMapsLink: "",
    phoneNumber: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    phoneNumber: "",
    region: "",
    location: "",
    customLocation: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [isCustomLocation, setIsCustomLocation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  useEffect(() => {
    if (currentBranch.region) {
      const locations = locationsByRegion[currentBranch.region] || [];
      setAvailableLocations([...locations, "Other"]);
    }
  }, [currentBranch.region]);

  // Access denied screen for users without create permissions
  if (!isAuthenticated || !canCreateBranches) {
    return (
      <div className="bg-gradient-to-br from-gray-50 to-[#FF6F6E]/5">
        <Navbar page="Add New Branch" />
        <div className="flex justify-center items-center min-h-[80vh]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-8 max-w-md text-center"
          >
            <div className="inline-block p-4 mb-6 bg-red-100 rounded-full">
              <Shield className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="mb-4 text-2xl font-bold text-gray-800">
              Access Restricted
            </h2>
            <p className="mb-6 leading-relaxed text-gray-600">
              You don't have permission to create new branches. Please contact
              your administrator to request access to the Branch Creation
              feature.
            </p>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex gap-2 items-center mb-2">
                <Users className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-800">
                  Required Permission:
                </span>
              </div>
              <p className="text-sm text-orange-700">
                Create Branch in Branch Management
              </p>
            </div>
            <div className="flex gap-3 mt-6">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate("/branches")}
                className="px-4 py-2 font-medium text-white bg-gray-500 rounded-lg transition-colors hover:bg-gray-600"
              >
                View Branches
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate("/dashboard")}
                className="px-4 py-2 bg-gradient-to-r from-[#FF6F6E] to-[#FF9A99] text-white rounded-lg hover:from-[#E55A59] hover:to-[#FF8584] transition-colors font-medium"
              >
                Return to Dashboard
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const validateBranchName = (name) => {
    if (!name.trim()) return "Branch name is required";
    if (name.length < 3) return "Branch name must be at least 3 characters";
    if (name.length > 50) return "Branch name must be less than 50 characters";
    return "";
  };

  const validatePhoneNumber = (number) => {
    if (!number) return "Phone number is required";
    if (number.length !== 9) return "Phone number must be exactly 9 digits";
    if (!/^\d+$/.test(number)) return "Phone number must contain only digits";
    return "";
  };

  const validateLocation = () => {
    if (!currentBranch.region) return "Region is required";
    if (!currentBranch.location && !isCustomLocation)
      return "Location is required";
    if (isCustomLocation && !currentBranch.customLocation.trim())
      return "Custom location is required";
    return "";
  };

  const handleRegionChange = (region) => {
    setCurrentBranch((prev) => ({
      ...prev,
      region,
      location: "",
      customLocation: "",
    }));
    setIsCustomLocation(false);
    setErrors((prev) => ({ ...prev, region: "", location: "" }));
  };

  const handleLocationChange = (location) => {
    setCurrentBranch((prev) => ({
      ...prev,
      location,
      customLocation: "",
    }));
    setIsCustomLocation(location === "Other");
    setErrors((prev) => ({ ...prev, location: "", customLocation: "" }));
  };

  const validateBranchForm = () => {
    const newErrors = {
      name: validateBranchName(currentBranch.name),
      phoneNumber: validatePhoneNumber(currentBranch.phoneNumber),
      location: validateLocation(),
    };

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return !Object.values(newErrors).some(Boolean);
  };

  const applyBranch = () => {
    if (!validateBranchForm()) return;

    const branchToAdd = {
      ...currentBranch,
      location: isCustomLocation
        ? currentBranch.customLocation
        : currentBranch.location,
      phoneNumber: currentBranch.phoneNumber.startsWith("233")
        ? currentBranch.phoneNumber
        : `233${currentBranch.phoneNumber}`,
      id: currentBranch.id || Date.now(),
    };

    setBranches((prev) =>
      isEditing
        ? prev.map((branch) =>
            branch.id === branchToAdd.id ? branchToAdd : branch
          )
        : [...prev, branchToAdd]
    );

    // Reset form
    setCurrentBranch({
      id: null,
      name: "",
      region: "",
      location: "",
      customLocation: "",
      district: "",
      suburb: "",
      landmark: "",
      gpsAddress: "",
      googleMapsLink: "",
      phoneNumber: "",
    });
    setIsEditing(false);
    setIsCustomLocation(false);
    setErrors({});

    toast.success(
      isEditing ? "Branch updated successfully" : "Branch added successfully",
      {
        position: "top-right",
        style: { background: "#10B981", color: "white" },
      }
    );
  };

  const removeBranch = (branchId) => {
    setBranches((prev) => prev.filter((branch) => branch.id !== branchId));
    toast.info("Branch removed", {
      position: "top-right",
      style: { background: "#6B7280", color: "white" },
    });
  };

  const handleEditBranch = (branch) => {
    const isLocationCustom = !locationsByRegion[branch.region]?.includes(
      branch.location
    );

    setCurrentBranch({
      ...branch,
      phoneNumber: branch.phoneNumber.startsWith("233")
        ? branch.phoneNumber.replace("233", "")
        : branch.phoneNumber,
      customLocation: isLocationCustom ? branch.location : "",
      location: isLocationCustom ? "Other" : branch.location,
    });
    setIsEditing(true);
    setIsCustomLocation(isLocationCustom);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate branches
    if (branches.length === 0) {
      toast.error("Please add at least one branch");
      return;
    }

    try {
      setIsLoading(true);

      const branchesPayload = branches.map((branch) => ({
        name: branch.name,
        region: branch.region,
        location: branch.location,
        district: branch.district || "",
        suburb: branch.suburb || "",
        landmark: branch.landmark || "",
        gpsAddress: branch.gpsAddress || "",
        google_url: branch.googleMapsLink || "",
        phone_number: branch.phoneNumber,
      }));

      const payload = {
        branches: branchesPayload,
      };

      const response = await branchService.addBranch(payload);
      if (
        response.status &&
        response.data?.status_code === "001" &&
        !response.data?.in_error
      ) {
        toast.success(
          response.data?.reason ||
            `${branches.length} branch${
              branches.length > 1 ? "es" : ""
            } added successfully`
        );
        navigate("/branches");
      } else {
        throw new Error(response.message || "Failed to add branches");
      }
    } catch (error) {
      console.error("Error adding branches:", error);
      toast.error(error.message || "Failed to add branches");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Navbar page="Add New Branch" />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gradient-to-br from-gray-50 to-[#FF6F6E]/5"
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="p-6 py-8 mx-auto max-w-7xl"
        >
          {/* Back Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/branches")}
            className="flex gap-2 items-center mb-6 text-gray-600 transition-colors hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Branches</span>
          </motion.button>

          <motion.div
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 gap-6 lg:grid-cols-2"
          >
            {/* Left Side - Form */}
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="sticky top-6 p-6 bg-white rounded-3xl border border-gray-100 shadow-xl"
              >
                <div className="space-y-4">
                  <div className="flex gap-2 items-center mb-4">
                    <div className="p-1.5 rounded-lg bg-[#FF6F6E]/10">
                      <MapPin className="w-5 h-5 text-[#FF6F6E]" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-800">
                      {isEditing ? "Edit Branch" : "Add New Branch"}
                    </h2>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        Branch Name
                      </label>
                      <input
                        type="text"
                        value={currentBranch.name}
                        onChange={(e) => {
                          setCurrentBranch((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }));
                          setErrors((prev) => ({ ...prev, name: "" }));
                        }}
                        className={`w-full px-4 py-2.5 text-base border-2 ${
                          errors.name
                            ? "border-red-300 bg-red-50"
                            : "border-gray-200"
                        } rounded-xl focus:ring-[#FF6F6E] focus:border-[#FF6F6E] transition-all duration-200`}
                        placeholder="Enter branch name"
                      />
                      <AnimatePresence>
                        {errors.name && (
                          <ValidationError message={errors.name} />
                        )}
                      </AnimatePresence>
                    </div>

                    <div className="col-span-2">
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        Phone Number
                        <span className="text-[#FF6F6E] ml-1">*</span>
                      </label>
                      <div className="relative">
                        <div className="flex absolute inset-y-0 left-0 items-center pl-4 pointer-events-none">
                          <span className="text-gray-500">233</span>
                        </div>
                        <input
                          type="text"
                          value={currentBranch.phoneNumber}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            if (value.length <= 9) {
                              setCurrentBranch((prev) => ({
                                ...prev,
                                phoneNumber: value,
                              }));
                              setErrors((prev) => ({
                                ...prev,
                                phoneNumber: "",
                              }));
                            }
                          }}
                          className={`w-full pl-14 pr-4 py-2.5 text-base border-2 ${
                            errors.phoneNumber
                              ? "border-red-300 bg-red-50"
                              : "border-gray-200"
                          } rounded-xl focus:ring-[#FF6F6E] focus:border-[#FF6F6E] transition-all duration-200`}
                          placeholder="Enter phone number"
                          required
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Enter 9 digits without the prefix
                      </p>
                      <AnimatePresence>
                        {errors.phoneNumber && (
                          <ValidationError message={errors.phoneNumber} />
                        )}
                      </AnimatePresence>
                    </div>

                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        Region
                      </label>
                      <div className="relative">
                        <select
                          value={currentBranch.region}
                          onChange={(e) => handleRegionChange(e.target.value)}
                          className={`w-full px-4 py-2.5 text-base border-2 ${
                            errors.region
                              ? "border-red-300 bg-red-50"
                              : "border-gray-200"
                          } rounded-xl focus:ring-[#FF6F6E] focus:border-[#FF6F6E] transition-all duration-200 appearance-none bg-white hover:border-gray-300 cursor-pointer`}
                        >
                          <option value="">Select Region</option>
                          {regionOptions.map((region) => (
                            <option key={region} value={region}>
                              {region}
                            </option>
                          ))}
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none p-1.5 rounded-lg bg-[#FF6F6E]/10">
                          <ChevronDown className="w-4 h-4 text-[#FF6F6E]" />
                        </div>
                      </div>
                      <AnimatePresence>
                        {errors.region && (
                          <ValidationError message={errors.region} />
                        )}
                      </AnimatePresence>
                    </div>

                    {currentBranch.region && (
                      <div>
                        <label className="block mb-1.5 text-sm font-medium text-gray-700">
                          Location
                        </label>
                        <div className="relative">
                          <select
                            value={currentBranch.location}
                            onChange={(e) =>
                              handleLocationChange(e.target.value)
                            }
                            className={`w-full px-4 py-2.5 text-base border-2 ${
                              errors.location
                                ? "border-red-300 bg-red-50"
                                : "border-gray-200"
                            } rounded-xl focus:ring-[#FF6F6E] focus:border-[#FF6F6E] transition-all duration-200 appearance-none bg-white hover:border-gray-300 cursor-pointer`}
                          >
                            <option value="">Select Location</option>
                            {availableLocations.map((location) => (
                              <option key={location} value={location}>
                                {location}
                              </option>
                            ))}
                          </select>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none p-1.5 rounded-lg bg-[#FF6F6E]/10">
                            <ChevronDown className="w-4 h-4 text-[#FF6F6E]" />
                          </div>
                        </div>
                        <AnimatePresence>
                          {errors.location && (
                            <ValidationError message={errors.location} />
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>

                  {isCustomLocation && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        Custom Location
                      </label>
                      <input
                        type="text"
                        value={currentBranch.customLocation}
                        onChange={(e) =>
                          setCurrentBranch((prev) => ({
                            ...prev,
                            customLocation: e.target.value,
                          }))
                        }
                        className={`w-full px-4 py-2.5 text-base border-2 ${
                          errors.customLocation
                            ? "border-red-300 bg-red-50"
                            : "border-gray-200"
                        } rounded-xl focus:ring-[#FF6F6E] focus:border-[#FF6F6E] transition-all duration-200`}
                        placeholder="Enter custom location"
                      />
                      <AnimatePresence>
                        {errors.customLocation && (
                          <ValidationError message={errors.customLocation} />
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}

                  {/* Optional Fields in Grid */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                    <h3 className="col-span-2 text-sm font-medium text-gray-700">
                      Additional Details
                    </h3>

                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        District <OptionalLabel />
                      </label>
                      <input
                        type="text"
                        value={currentBranch.district}
                        onChange={(e) =>
                          setCurrentBranch((prev) => ({
                            ...prev,
                            district: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl focus:ring-[#FF6F6E] focus:border-[#FF6F6E] transition-all duration-200"
                        placeholder="Enter district name"
                      />
                    </div>

                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        Suburb <OptionalLabel />
                      </label>
                      <input
                        type="text"
                        value={currentBranch.suburb}
                        onChange={(e) =>
                          setCurrentBranch((prev) => ({
                            ...prev,
                            suburb: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl focus:ring-[#FF6F6E] focus:border-[#FF6F6E] transition-all duration-200"
                        placeholder="Enter suburb name"
                      />
                    </div>

                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        Landmark <OptionalLabel />
                      </label>
                      <input
                        type="text"
                        value={currentBranch.landmark}
                        onChange={(e) =>
                          setCurrentBranch((prev) => ({
                            ...prev,
                            landmark: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl focus:ring-[#FF6F6E] focus:border-[#FF6F6E] transition-all duration-200"
                        placeholder="Enter a nearby landmark"
                      />
                    </div>

                    <div>
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        GPS Address <OptionalLabel />
                      </label>
                      <input
                        type="text"
                        value={currentBranch.gpsAddress}
                        onChange={(e) =>
                          setCurrentBranch((prev) => ({
                            ...prev,
                            gpsAddress: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl focus:ring-[#FF6F6E] focus:border-[#FF6F6E] transition-all duration-200"
                        placeholder="Enter GPS address"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block mb-1.5 text-sm font-medium text-gray-700">
                        Google Maps Link <OptionalLabel />
                      </label>
                      <input
                        type="url"
                        value={currentBranch.googleMapsLink}
                        onChange={(e) =>
                          setCurrentBranch((prev) => ({
                            ...prev,
                            googleMapsLink: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl focus:ring-[#FF6F6E] focus:border-[#FF6F6E] transition-all duration-200"
                        placeholder="https://maps.google.com/..."
                      />
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={applyBranch}
                    disabled={
                      !currentBranch.name ||
                      !currentBranch.region ||
                      (!currentBranch.location &&
                        !currentBranch.customLocation) ||
                      !currentBranch.phoneNumber
                    }
                    className="w-full py-3 bg-gradient-to-r from-[#FF6F6E] to-[#FF9A99] text-white rounded-xl flex items-center justify-center gap-2 hover:from-[#E55A59] hover:to-[#FF8584] transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg group mt-4"
                  >
                    <Check className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" />
                    {isEditing ? "Update Branch" : "Add Branch"}
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Side - Preview */}
            <motion.div
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col h-full"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="flex-1 p-6 mb-6 bg-white rounded-3xl border border-gray-100 shadow-xl"
              >
                <div className="flex justify-between items-center mb-6">
                  <div className="flex gap-3 items-center">
                    <div className="p-2 rounded-xl bg-[#FF6F6E]/10">
                      <Building2 className="w-5 h-5 text-[#FF6F6E]" />
                    </div>
                    <h2 className="text-lg font-semibold text-gray-800">
                      Branch Preview
                    </h2>
                  </div>
                  {branches.length > 0 && (
                    <div className="px-3 py-1.5 bg-[#FF6F6E]/10 rounded-xl">
                      <span className="text-sm font-medium text-[#FF6F6E]">
                        {branches.length}{" "}
                        {branches.length === 1 ? "Branch" : "Branches"}
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {branches.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <AnimatePresence>
                      {branches.map((branch) => (
                        <BranchPreview
                          key={branch.id}
                          branchData={branch}
                          onEdit={handleEditBranch}
                          onRemove={removeBranch}
                          canEdit={true}
                          canDelete={true}
                        />
                      ))}
                    </AnimatePresence>
                  )}
                </div>
              </motion.div>

              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={handleSubmit}
                disabled={branches.length === 0 || isLoading}
                className="w-full py-4 bg-gradient-to-r from-[#FF6F6E] to-[#FF9A99] text-white text-lg font-medium rounded-xl flex items-center justify-center gap-3 hover:from-[#E55A59] hover:to-[#FF8584] transition-all duration-200 shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-xl group"
              >
                <Save className="w-6 h-6 transition-transform duration-200 group-hover:scale-110" />
                {isLoading ? (
                  <div className="flex gap-2 items-center">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating Branches...
                  </div>
                ) : (
                  `Create ${branches.length} Branch${
                    branches.length !== 1 ? "es" : ""
                  }`
                )}
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Add a clear error states button when editing */}
      {isEditing && (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => {
            setCurrentBranch({
              id: null,
              name: "",
              region: "",
              location: "",
              customLocation: "",
              district: "",
              suburb: "",
              landmark: "",
              gpsAddress: "",
              phoneNumber: "",
            });
            setIsEditing(false);
            setIsCustomLocation(false);
            setErrors({});
          }}
          className="fixed right-6 top-24 p-2 text-gray-600 bg-white rounded-full border border-gray-200 shadow-lg transition-all duration-200 hover:shadow-xl hover:text-gray-900"
        >
          <X className="w-5 h-5" />
        </motion.button>
      )}
    </>
  );
};

export default BranchAdd;
